import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import Dropzone from "react-dropzone";
import SuperAgent from "superagent";
import TeamEmailsSelect from "~/components/forms/dropdowns/TeamEmailsSelect";
import EmailEditor from "~/components/forms/HtmlEditors/EmailEditor";
import EmailTemplateSelect from "~/components/forms/Templates/EmailTemplateSelect";
import InsertDocumentButton from "~/components/insert_document_button";
import { NOOP_FALSE } from "~/constants/callbacks";
import APIRequest from "~/lib/api_request";
import CopyRecipientNotice, {
  COPY_RECIPIENT_NOTICE_MODES,
} from "./CopyRecipientNotice";
import EmailRecipientField from "./EmailRecipientField";

class EmailForm extends React.Component {
  constructor(props) {
    super(props);

    const {
      from = "",
      subject = "",
      body = "",
      cc_recipients = [],
      bcc_recipients = [],
      references = [],
      attachments = [],
    } = props;

    this.state = {
      errors: {},
      from,
      subject,
      body,
      cc_recipients,
      bcc_recipients,
      references,
      attachments,
      attachment_ids: attachments.map((a) => a.id) || [],
      attachmentsSize: null,
      attachmentsError: null,
      attachmentsLoading: false,
      recipientFields: {
        cc: cc_recipients?.length > 0,
        bcc: bcc_recipients?.length > 0,
      },
      enableEmailTemplate: true,
    };

    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      subject,
      body,
      attachments,
      cc_recipients,
      bcc_recipients,
      references,
    } = this.props;
    const {
      subject: prevSubject,
      body: prevBody,
      attachments: prevAttachments,
      cc_recipients: prevCc,
      bcc_recipients: prevBcc,
      references: prevReferences,
    } = prevProps;

    const hasPropsChanged =
      subject !== prevSubject ||
      body !== prevBody ||
      attachments !== prevAttachments ||
      cc_recipients !== prevCc ||
      bcc_recipients !== prevBcc ||
      references !== prevReferences;

    if (!hasPropsChanged) {
      return;
    }

    // Skip setState if props already match current state (change originated internally)
    const { state } = this;
    const isAlreadyInSync =
      subject === state.subject &&
      body === state.body &&
      cc_recipients === state.cc_recipients &&
      bcc_recipients === state.bcc_recipients &&
      references === state.references &&
      attachments === state.attachments;

    if (isAlreadyInSync) {
      return;
    }

    const newState = {
      subject,
      body,
      cc_recipients,
      bcc_recipients,
      references,
    };

    if (cc_recipients !== prevCc || bcc_recipients !== prevBcc) {
      newState.recipientFields = {
        cc: cc_recipients?.length > 0,
        bcc: bcc_recipients?.length > 0,
      };
    }

    if (attachments !== prevAttachments) {
      newState.attachments = attachments || [];
      newState.attachment_ids = attachments.map((a) => a.id) || [];
    }

    this.setState(newState);
  }

  handleToggleRecipientField(type, show) {
    const { onChange } = this.props;
    const stateKey = `${type}_recipients`;

    this.setState(
      (prevState) => ({
        recipientFields: {
          ...prevState.recipientFields,
          [type]: show,
        },
        ...(!show && { [stateKey]: [] }),
      }),
      () => {
        if (!show) {
          onChange(this.getEmailState(this.state));
        }
      },
    );
  }

  handleEmailChange(type, selectedOptions) {
    const { onChange, enableCCField, enableBCCField } = this.props;
    const isEnabled = type === "cc" ? enableCCField : enableBCCField;

    if (!isEnabled) {
      return;
    }

    const stateKey = `${type}_recipients`;

    this.setState(
      (prevState) => ({
        ...prevState,
        [stateKey]: selectedOptions,
      }),
      () => onChange(this.getEmailState(this.state)),
    );
  }

  onRemoveAttachment(attachmentID) {
    let { attachments } = this.state;

    attachments = attachments.filter((a) => a.id !== attachmentID);

    this.onAttachmentsChange(attachments);
  }

  onDrop(files) {
    this.setState({ attachmentsError: null, attachmentsLoading: true });

    const { attachments, attachmentsSize } = this.state;

    // We have to do a special request here since we need to multipart the
    // uploads, and api request by default tries to set everything as JSON
    const req = SuperAgent.post(`${Rails.apiUrl}/api/v1/attachments`);
    req.set(APIRequest.apiRequiredHeaders());
    files.forEach((file) => {
      req.attach("attachment[][file]", file);
    });

    req.end((err, res) => {
      const a = _lodash
        .zip(files, res.body)
        .map((ary) => _lodash.extend(ary[0], ary[1]));
      const size = _lodash.sumBy(a, "size") / 1048576 + (attachmentsSize || 0);
      const updatedAttachments = _lodash.concat(attachments, a);
      if (size < 5.0) {
        this.setState(
          {
            attachmentsSize: size,
            attachments: updatedAttachments,
            attachmentsLoading: false,
          },
          () => {
            this.onAttachmentsChange(updatedAttachments);
          },
        );
      } else {
        this.setState({
          attachmentsError:
            "Sorry, Google only supports emailing with attachments under 5MB total. " +
            "Try uploading less than 5MB worth of attachments.",
          attachmentsLoading: false,
        });
      }
    });
  }

  onFromChange(selectedItem) {
    const { onChange, validateFromDomainAndRecipients } = this.props;
    const from = selectedItem?.value || null;
    this.setState(
      (prevState) => ({ ...prevState, from }),
      () => {
        onChange(this.getEmailState(this.state));
        if (validateFromDomainAndRecipients) {
          validateFromDomainAndRecipients(from);
        }
      },
    );
  }

  onSubjectChange(e) {
    e.preventDefault();

    const { onChange } = this.props;
    const subject = e.target.value;
    this.setState(
      (prevState) => ({ ...prevState, subject }),
      () => {
        onChange(this.getEmailState(this.state));
      },
    );
  }

  onBodyChange(html) {
    const { onChange } = this.props;
    this.setState(
      (prevState) => ({ ...prevState, body: html }),
      () => {
        onChange(this.getEmailState(this.state));
      },
    );
  }

  onAttachmentsChange(attachments) {
    const { onChange } = this.props;
    const attachment_ids = attachments.map((a) => a.id);
    this.setState(
      (prevState) => ({ ...prevState, attachments, attachment_ids }),
      () => {
        onChange(this.getEmailState(this.state));
      },
    );
  }

  onDismissSignatureNag(e) {
    e.preventDefault();

    localStorage.setItem("emailForm.hideSignatureNag", "1");
    this.forceUpdate();
  }

  getEmailState(prevState) {
    const {
      from,
      subject,
      body,
      cc_recipients,
      bcc_recipients,
      attachment_ids,
      references,
      attachments,
    } = prevState;
    return {
      from,
      subject,
      body,
      cc_recipients,
      bcc_recipients,
      attachment_ids,
      references,
      attachments,
    };
  }

  setSubject(subject) {
    this.setState({ subject });
  }

  validate(isTemplate = false) {
    const { enableFromField } = this.props;
    const { from, subject, body } = this.state;
    const errors = {};

    if (enableFromField && !from && !isTemplate) {
      errors.from = "Can't be empty";
    }

    if (!subject) {
      errors.subject = "Can't be empty";
    }

    if (!body) {
      errors.body = "Can't be empty";
    }

    this.setState({ errors });
    return errors;
  }

  applyTemplate(template) {
    const { onChange } = this.props;

    const newState = template
      ? {
          subject: template.subject || "",
          body: template.body || "",
          attachments: template.attachments || [],
          attachment_ids: (template.attachments || []).map((a) => a.id),
          templateBody: template.body,
          errors: {},
        }
      : {
          // Clearing template does not wipe subject — preserve user's typed subject.
          body: "",
          attachments: [],
          templateBody: null,
          attachment_ids: [],
          errors: {},
        };

    this.setState(newState, () => {
      onChange(this.getEmailState(this.state));
    });
  }

  renderExtraFeatures() {
    const { cc_recipients, bcc_recipients, recipientFields } = this.state;
    const { enableCCField, enableBCCField, recipientNoticeMode } = this.props;
    const showRecipientNotice =
      recipientNoticeMode &&
      ((recipientFields.cc && enableCCField) ||
        (recipientFields.bcc && enableBCCField));

    return (
      <div>
        {recipientFields.cc && enableCCField && (
          <EmailRecipientField
            type="cc"
            value={cc_recipients}
            onChange={(selected) => this.handleEmailChange("cc", selected)}
            onHide={() => this.handleToggleRecipientField("cc", false)}
          />
        )}
        {recipientFields.bcc && enableBCCField && (
          <EmailRecipientField
            type="bcc"
            value={bcc_recipients}
            onChange={(selected) => this.handleEmailChange("bcc", selected)}
            onHide={() => this.handleToggleRecipientField("bcc", false)}
          />
        )}
        {showRecipientNotice && (
          <CopyRecipientNotice mode={recipientNoticeMode} />
        )}
      </div>
    );
  }

  renderExtraFeatureMenu() {
    const { recipientFields } = this.state;
    const { enableCCField, enableBCCField } = this.props;

    return (
      <div className="d-flex pull-right">
        {!recipientFields.cc && enableCCField && (
          <span className="mb5 mr-2">
            <a
              href="#add-emails"
              className="text-black"
              onClick={() => this.handleToggleRecipientField("cc", true)}
            >
              + CC
            </a>
          </span>
        )}

        {!recipientFields.bcc && enableBCCField && (
          <span className="mb5">
            <a
              href="#add-bcc-emails"
              className="text-black"
              onClick={() => this.handleToggleRecipientField("bcc", true)}
            >
              + BCC
            </a>
          </span>
        )}
      </div>
    );
  }

  renderAttachments(attachments) {
    return attachments.map((f) => (
      <div className="alert alert-success p10 text-left mb5" key={f.name}>
        <div className="pull-right">
          <a
            href="#remove-attachment"
            onClick={(e) => {
              e.preventDefault();
              this.onRemoveAttachment(f.id);
            }}
          >
            <FontAwesomeIcon icon={["far", "fa-times"]} />
          </a>
        </div>
        <div className="text-break">
          <FontAwesomeIcon icon={["far", "fa-file"]} className="mr10" />
          {f.name}
        </div>
      </div>
    ));
  }

  render() {
    const {
      from,
      subject,
      body,
      errors,
      templateBody,
      attachmentsLoading,
      attachments,
      attachmentsError,
      enableEmailTemplate,
      linksToInsert,
    } = this.state;
    const { enableFromField, disableSignature, contactOwner, leadId } =
      this.props;

    const { currentUser } = Rails.helpers;
    const { signature } = currentUser;

    const emailContent = body || "";
    const signatureContent = signature || "";

    let emailValue;

    if (!_lodash.isEmpty(emailContent) || disableSignature) {
      emailValue = emailContent;
    } else {
      emailValue = `${emailContent}${signatureContent}`;
    }
    const showSignatureNag =
      !signature && localStorage.getItem("emailForm.hideSignatureNag") !== "1";

    return (
      <div>
        {enableFromField && (
          <div className="form-group mb15">
            <label htmlFor="email_from" className="d-block">
              From
            </label>
            <TeamEmailsSelect
              id="email_from"
              name="from"
              value={from}
              isInvalid={!!errors.from}
              isSearchable
              onChange={this.onFromChange}
              contactOwner={contactOwner}
            />
          </div>
        )}

        {this.renderExtraFeatures()}

        <div className="form-group mb15">
          {this.renderExtraFeatureMenu()}
          <label htmlFor="email_subject" className="d-block">
            Subject
          </label>
          <input
            id="email_subject"
            type="text"
            name="subject"
            className={`form-control ${errors.subject ? "has-error" : ""}`}
            value={subject}
            onChange={this.onSubjectChange}
          />
        </div>

        <div className="form-group mb15">
          <label htmlFor="html-body" className="d-block">
            Body
          </label>
          <EmailEditor
            className={errors.body ? "tiny-mce has-error" : ""}
            value={emailValue}
            onChange={this.onBodyChange}
            setSubject={this.setSubject}
            leadId={leadId}
            replaceValue={templateBody}
            clearReplaceValue={() => {
              this.setState({
                templateBody: null,
              });
            }}
            linksToInsert={linksToInsert}
            clearLinksToInsert={() => {
              this.setState({ linksToInsert: [] });
            }}
          />

          {showSignatureNag && (
            <p className="mt10 text-grey">
              <span>Need to add a signature? Add it in </span>
              <a
                href="/users/edit"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black font-weight-bold"
              >
                User Settings
              </a>
              <span> or </span>
              <a
                href="#dismiss"
                className="font-weight-bold"
                onClick={this.onDismissSignatureNag}
              >
                dismiss this
              </a>
              .
            </p>
          )}
        </div>

        <div className="row m0">
          <div className="col-lg-6 p0">
            <div className="form-group m0">
              <Dropzone onDrop={this.onDrop}>
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps({
                      className: "btn btn-secondary mb5 mr-2",
                    })}
                  >
                    <input {...getInputProps()} />
                    {attachmentsLoading ? (
                      <FontAwesomeIcon
                        icon="far fa-spinner"
                        pulse
                        className="mr5"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={["far", "fa-plus"]}
                        className="mr5"
                      />
                    )}
                    Add Attachment
                  </div>
                )}
              </Dropzone>
              <InsertDocumentButton
                setLinksToInsert={(links) =>
                  this.setState({ linksToInsert: links })
                }
              />

              {this.renderAttachments(attachments)}

              {attachmentsError && (
                <div className="alert alert-danger">{attachmentsError}</div>
              )}
            </div>
          </div>

          <div className="col-lg-4 p0 pl0-sm-down ml-auto">
            {enableEmailTemplate && (
              <EmailTemplateSelect
                subject={subject}
                body={body}
                attachments={attachments}
                applyTemplate={this.applyTemplate}
                validateEmailTemplate={this.validate}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

EmailForm.defaultProps = {
  errors: {},
  from: "",
  subject: "",
  body: "",
  attachments: [],
  references: [],
  disableSignature: false,
  enableFromField: false,
  enableCCField: false,
  enableBCCField: false,
  contactOwner: false,
  leadId: null,
  onChange: NOOP_FALSE,
  validateFromDomainAndRecipients: NOOP_FALSE,
  recipientNoticeMode: null,
};

EmailForm.propTypes = {
  errors: PropTypes.shape({}),
  from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subject: PropTypes.string,
  body: PropTypes.string,
  attachments: PropTypes.arrayOf(PropTypes.shape({})),
  references: PropTypes.arrayOf(PropTypes.string),
  disableSignature: PropTypes.bool,
  enableFromField: PropTypes.bool,
  enableCCField: PropTypes.bool,
  enableBCCField: PropTypes.bool,
  contactOwner: PropTypes.bool,
  leadId: PropTypes.number,
  onChange: PropTypes.func,
  validateFromDomainAndRecipients: PropTypes.func,
  recipientNoticeMode: PropTypes.oneOf([
    ...Object.values(COPY_RECIPIENT_NOTICE_MODES),
    null,
  ]),
};

export default EmailForm;
